(() => {
  const root = document.querySelector('[data-samba-directory]');
  const payload = window.ACSambaDirectory;
  if (!root || !payload?.schools) return;

  const search = root.querySelector('[data-directory-search]');
  const group = root.querySelector('[data-directory-group]');
  const status = root.querySelector('[data-directory-status]');
  const body = root.querySelector('[data-directory-body]');
  const count = root.querySelector('[data-directory-count]');
  const previous = root.querySelector('[data-directory-previous]');
  const next = root.querySelector('[data-directory-next]');
  const pageLabel = root.querySelector('[data-directory-page]');
  const references = root.querySelector('[data-directory-references]');
  const referenceHeading = root.querySelector('[data-directory-reference-heading]');
  const detailAssetBase = root.dataset.schoolAssetBase || '';
  const sourceById = new Map();
  const detailPromises = new Map();
  const pageSize = 30;
  let page = 1;

  const loadSchoolDetail = (school) => {
    if (school.detailLoaded) return Promise.resolve(school);
    if (detailPromises.has(school.id)) return detailPromises.get(school.id);
    if (!detailAssetBase) return Promise.reject(new Error('School detail asset base is missing.'));

    const assetName = `${payload.detailAssetPrefix || 'ac-samba-school-'}${school.id}${payload.detailAssetSuffix || '.json'}`;
    const detailUrl = new URL(`${detailAssetBase}${assetName}`, window.location.href);
    const request = fetch(detailUrl, { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Unable to load school detail (${response.status}).`);
        return response.json();
      })
      .then((detail) => {
        school.claims = detail.claims ?? [];
        school.story = detail.story ?? null;
        (detail.sources ?? []).forEach((source) => sourceById.set(source.id, source));
        school.detailLoaded = true;
        return school;
      })
      .catch((error) => {
        detailPromises.delete(school.id);
        throw error;
      });
    detailPromises.set(school.id, request);
    return request;
  };

  const escapeText = (value) => String(value ?? '');
  const normalise = (value) => escapeText(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const statusLabel = (school) => {
    if (school.current_division && school.current_division !== 'Inativas ou Extintas') return school.current_division;
    if (school.status === 'inactive') return 'Inactive';
    if (school.status === 'extinct') return 'Extinct';
    if (school.status === 'succeeded_or_merged') return 'Merged / succeeded';
    return 'Inactive / extinct — status under review';
  };
  const divisionCategory = (school) => {
    const division = normalise(school.current_division);
    const categories = [
      ['Grupo Especial', 'grupo especial'],
      ['Série Ouro', 'serie ouro'],
      ['Série Prata', 'serie prata'],
      ['Série Bronze', 'serie bronze'],
      ['Grupo B', 'grupo b'],
      ['Grupo de Avaliação', 'grupo de avaliacao'],
    ];
    const match = categories.find(([, token]) => division.includes(token));
    if (match) return match[0];
    if (school.status !== 'active') return 'Inactive / extinct';
    if (division.includes('liga sg')) return 'Other active / regional';
    return 'Active / division under review';
  };
  const makeCell = (value, className = '') => {
    const cell = document.createElement('td');
    if (className) cell.className = className;
    cell.textContent = value || '—';
    return cell;
  };
  const addDetail = (list, label, value) => {
    if (!value) return;
    const row = document.createElement('div');
    const term = document.createElement('dt');
    const description = document.createElement('dd');
    term.textContent = label;
    description.textContent = value;
    row.append(term, description);
    list.append(row);
  };
  const appendInlineStoryText = (element, value) => {
    const parts = escapeText(value).split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g).filter(Boolean);
    parts.forEach((part) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        const strong = document.createElement('strong');
        strong.textContent = part.slice(2, -2);
        element.append(strong);
      } else if (part.startsWith('`') && part.endsWith('`')) {
        const code = document.createElement('code');
        code.textContent = part.slice(1, -1);
        element.append(code);
      } else if (part.startsWith('*') && part.endsWith('*')) {
        const emphasis = document.createElement('em');
        emphasis.textContent = part.slice(1, -1);
        element.append(emphasis);
      } else {
        element.append(document.createTextNode(part));
      }
    });
  };
  const addStoryMedia = (container, media) => {
    const figure = document.createElement('figure');
    const image = document.createElement('img');
    const caption = document.createElement('figcaption');
    figure.className = 'ac-samba-directory__story-media';
    image.src = media.asset_url;
    image.alt = media.alt_text_pt || '';
    image.loading = 'lazy';
    image.decoding = 'async';
    caption.textContent = media.caption_pt;
    figure.append(image, caption);
    container.append(figure);
  };
  const addStory = (container, school, beforeNode = null) => {
    if (!school.story?.sections?.length) return;

    const disclosure = document.createElement('details');
    const summary = document.createElement('summary');
    const article = document.createElement('article');
    const title = document.createElement('h3');
    const standfirst = document.createElement('p');
    const metadata = document.createElement('p');
    disclosure.className = 'ac-samba-directory__story';
    summary.textContent = 'Curiosidades e histórias — ler o dossiê';
    article.className = 'ac-samba-directory__story-article';
    title.textContent = school.story.title;
    standfirst.className = 'ac-samba-directory__story-standfirst';
    standfirst.textContent = school.story.standfirst || '';
    metadata.className = 'ac-samba-directory__story-meta';
    metadata.textContent = `Dossiê documental · ${school.story.word_count.toLocaleString('pt-BR')} palavras`;
    article.append(title);
    if (standfirst.textContent) article.append(standfirst);
    article.append(metadata);

    (school.story.media ?? [])
      .filter((media) => !/^after-section-\d+$/.test(media.placement || ''))
      .forEach((media) => addStoryMedia(article, media));

    school.story.sections.forEach((section) => {
      const sectionElement = document.createElement('section');
      const heading = document.createElement('h4');
      heading.textContent = section.heading;
      sectionElement.className = 'ac-samba-directory__story-section';
      sectionElement.append(heading);

      escapeText(section.body_markdown).split(/\n{2,}/).map((block) => block.trim()).filter(Boolean).forEach((block) => {
        const lines = block.split('\n').map((line) => line.trim()).filter(Boolean);
        if (lines.length && lines.every((line) => /^[-*]\s+/.test(line))) {
          const list = document.createElement('ul');
          lines.forEach((line) => {
            const item = document.createElement('li');
            appendInlineStoryText(item, line.replace(/^[-*]\s+/, ''));
            list.append(item);
          });
          sectionElement.append(list);
        } else {
          const paragraph = document.createElement('p');
          appendInlineStoryText(paragraph, lines.join(' '));
          sectionElement.append(paragraph);
        }
      });

      const sectionSourceIds = [...new Set((section.sources ?? []).map((entry) => entry.source_id))];
      if (sectionSourceIds.length) {
        const sourceDisclosure = document.createElement('details');
        const sourceSummary = document.createElement('summary');
        const sourceList = document.createElement('ol');
        sourceDisclosure.className = 'ac-samba-directory__story-sources';
        sourceSummary.textContent = `Fontes desta seção (${sectionSourceIds.length})`;
        sectionSourceIds.map((id) => sourceById.get(id)).filter(Boolean).forEach((source) => {
          const item = document.createElement('li');
          const link = document.createElement('a');
          link.href = source.url;
          link.target = '_blank';
          link.rel = 'noopener';
          link.textContent = source.title || source.url;
          item.append(link);
          if (source.publisher) item.append(document.createTextNode(` — ${source.publisher}`));
          sourceList.append(item);
        });
        sourceDisclosure.append(sourceSummary, sourceList);
        sectionElement.append(sourceDisclosure);
      }

      article.append(sectionElement);
      (school.story.media ?? [])
        .filter((media) => media.placement === `after-section-${section.section_order}`)
        .forEach((media) => addStoryMedia(article, media));
    });

    if (school.story.completeness_note) {
      const completeness = document.createElement('p');
      completeness.className = 'ac-samba-directory__story-completeness';
      completeness.textContent = `O que ainda falta documentar: ${school.story.completeness_note}`;
      article.append(completeness);
    }

    disclosure.append(summary, article);
    container.insertBefore(disclosure, beforeNode);
  };
  const humanise = (value) => escapeText(value)
    .replace(/^parade_/, '')
    .replaceAll('_', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const claimTypeLabels = {
    fact: 'Verified findings',
    name_period: 'Historical names',
    alias: 'Name variants',
    address: 'Historical addresses',
    affiliation: 'Federation and institutional affiliations',
    person_role: 'People documented',
    parade: 'Parade chronology',
    title: 'Titles',
    event: 'Other documented activity',
    relationship: 'Institutional relationships',
    legal_entity: 'Legal records',
    media: 'Documentary and visual records',
  };
  const hiddenDuplicateFacts = new Set([
    'directory_status', 'status', 'founded_date', 'extinct_date', 'colours',
    'symbol', 'instagram_url', 'website_url', 'logo_url',
  ]);
  const addArchivalRecord = (container, school, beforeNode = null) => {
    const unique = new Map();
    (school.claims ?? [])
      .filter((claim) => claim.reference_type !== 'fact' || !hiddenDuplicateFacts.has(claim.field_name))
      .forEach((claim) => {
        const key = [claim.reference_type, claim.field_name, claim.field_value].join('\u001f');
        if (!unique.has(key)) unique.set(key, claim);
      });
    if (!unique.size) return;

    const section = document.createElement('section');
    section.className = 'ac-samba-directory__archive';
    const heading = document.createElement('h3');
    heading.textContent = 'Documented archival record';
    section.append(heading);

    const grouped = new Map();
    unique.forEach((claim) => {
      const list = grouped.get(claim.reference_type) ?? [];
      list.push(claim);
      grouped.set(claim.reference_type, list);
    });
    Object.keys(claimTypeLabels).forEach((type) => {
      const claims = grouped.get(type);
      if (!claims?.length) return;
      const group = document.createElement('section');
      const groupHeading = document.createElement('h4');
      const list = document.createElement('ul');
      groupHeading.textContent = claimTypeLabels[type];
      claims.forEach((claim) => {
        const item = document.createElement('li');
        const label = document.createElement('strong');
        const meta = document.createElement('small');
        label.textContent = `${humanise(claim.field_name)}: `;
        item.append(label, document.createTextNode(claim.field_value));
        const metadata = [claim.confidence && `${claim.confidence} confidence`, claim.review_status]
          .filter(Boolean)
          .map(humanise);
        if (metadata.length) {
          meta.textContent = metadata.join(' · ');
          item.append(meta);
        }
        list.append(item);
      });
      group.append(groupHeading, list);
      section.append(group);
    });
    container.insertBefore(section, beforeNode);
  };
  const showReferences = (school) => {
    const sourceIds = [...new Set((school.claims ?? school.facts ?? []).map((claim) => claim.source_id))];
    referenceHeading.textContent = `References for ${school.name}`;
    references.replaceChildren();
    if (!sourceIds.length) {
      const note = document.createElement('p');
      note.textContent = 'No field-level source has been attached yet. This entry remains in the research queue.';
      references.append(note);
      return;
    }
    const list = document.createElement('ol');
    sourceIds.map((id) => sourceById.get(id)).filter(Boolean).forEach((source) => {
      const item = document.createElement('li');
      const link = document.createElement('a');
      link.href = source.url;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = source.title || source.url;
      item.append(link);
      const meta = [source.publisher, source.source_type, source.source_quality, source.retrieved_date && `retrieved ${source.retrieved_date}`].filter(Boolean);
      if (meta.length) item.append(document.createTextNode(` — ${meta.join(', ')}`));
      list.append(item);
    });
    references.append(list);
  };

  const filteredSchools = () => {
    const term = normalise(search.value.trim());
    return payload.schools.filter((school) => {
      const matchesSearch = !term || normalise([school.name, school.address, school.colours, school.symbol, school.status_notes].join(' ')).includes(term);
      const matchesGroup = group.value === 'all' || divisionCategory(school) === group.value;
      const matchesStatus = status.value === 'all'
        || (status.value === 'active' && school.status === 'active')
        || (status.value === 'historical' && school.status !== 'active');
      return matchesSearch && matchesGroup && matchesStatus;
    });
  };

  const render = () => {
    const filtered = filteredSchools();
    const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
    page = Math.min(page, totalPages);
    const visible = filtered.slice((page - 1) * pageSize, page * pageSize);
    body.replaceChildren();

    visible.forEach((school) => {
      const row = document.createElement('tr');
      row.id = `ac-samba-school-${school.id}`;
      const logoCell = document.createElement('td');
      logoCell.className = 'ac-samba-directory__logo';
      if (school.logo_url) {
        const image = document.createElement('img');
        image.src = school.logo_url;
        image.alt = `${school.name} crest`;
        image.loading = 'lazy';
        logoCell.append(image);
      }
      const nameCell = makeCell(school.name, 'ac-samba-directory__name');
      const actionCell = document.createElement('td');
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ac-samba-directory__view';
      button.textContent = 'View';
      button.setAttribute('aria-expanded', 'false');
      actionCell.append(button);
      row.append(logoCell, nameCell, makeCell(statusLabel(school)), makeCell(school.founded_date), makeCell(school.address), actionCell);

      const detailRow = document.createElement('tr');
      detailRow.className = 'ac-samba-directory__detail-row';
      detailRow.hidden = true;
      const detailCell = document.createElement('td');
      detailCell.colSpan = 6;
      const list = document.createElement('dl');
      list.className = 'ac-samba-directory__detail-list';
      addDetail(list, 'Status', school.status_notes || statusLabel(school));
      addDetail(list, 'Founded', school.founded_date);
      addDetail(list, 'Extinct', school.extinct_date);
      addDetail(list, 'Last-known address', school.address);
      addDetail(list, 'Colours', school.colours);
      addDetail(list, 'Symbol', school.symbol);
      addDetail(list, 'Research status', school.research_status?.replaceAll('_', ' '));
      const referenceButton = document.createElement('button');
      referenceButton.type = 'button';
      referenceButton.className = 'ac-samba-directory__references-button';
      referenceButton.textContent = 'Show references';
      referenceButton.addEventListener('click', async () => {
        referenceButton.disabled = true;
        referenceButton.textContent = 'Loading references…';
        try {
          await loadSchoolDetail(school);
          showReferences(school);
          root.querySelector('#ac-samba-references')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } catch (error) {
          referenceHeading.textContent = `References for ${school.name}`;
          references.textContent = `The references could not be loaded. ${error.message}`;
        } finally {
          referenceButton.disabled = false;
          referenceButton.textContent = 'Show references';
        }
      });
      detailCell.append(list, referenceButton);
      detailRow.append(detailCell);
      let archiveRendered = false;
      button.addEventListener('click', async () => {
        if (!detailRow.hidden) {
          detailRow.hidden = true;
          button.setAttribute('aria-expanded', 'false');
          button.textContent = 'View';
          return;
        }

        if (!archiveRendered) {
          button.disabled = true;
          button.textContent = 'Loading…';
          try {
            await loadSchoolDetail(school);
            addStory(detailCell, school, referenceButton);
            addArchivalRecord(detailCell, school, referenceButton);
            archiveRendered = true;
          } catch (error) {
            const errorMessage = document.createElement('p');
            errorMessage.className = 'ac-samba-directory__load-error';
            errorMessage.textContent = `The documented dossier could not be loaded. ${error.message}`;
            detailCell.insertBefore(errorMessage, referenceButton);
          } finally {
            button.disabled = false;
          }
        }

        detailRow.hidden = false;
        button.setAttribute('aria-expanded', 'true');
        button.textContent = 'Close';
      });
      body.append(row, detailRow);
    });

    count.textContent = `${filtered.length} schools`; 
    pageLabel.textContent = `Page ${page} of ${totalPages}`;
    previous.disabled = page === 1;
    next.disabled = page === totalPages;
  };

  const divisions = [...new Set(payload.schools.map(divisionCategory).filter(Boolean))].sort();
  divisions.forEach((division) => {
    const option = document.createElement('option');
    option.value = division;
    option.textContent = division;
    group.append(option);
  });
  [search, group, status].forEach((control) => control.addEventListener('input', () => { page = 1; render(); }));
  previous.addEventListener('click', () => { page -= 1; render(); root.scrollIntoView({ behavior: 'smooth' }); });
  next.addEventListener('click', () => { page += 1; render(); root.scrollIntoView({ behavior: 'smooth' }); });
  document.addEventListener('ac:samba:open-school', (event) => {
    const school = payload.schools.find((entry) => entry.id === Number(event.detail?.id));
    if (!school) return;
    search.value = school.name;
    group.value = 'all';
    status.value = 'all';
    page = 1;
    render();
    window.requestAnimationFrame(() => {
      const row = root.querySelector(`#ac-samba-school-${school.id}`);
      if (!row) return;
      row.querySelector('.ac-samba-directory__view')?.click();
      row.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });
  render();
})();
